import { useCallback, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItemIcon,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  DeleteOutline,
  Download,
  InfoOutlined,
  Link as LinkIcon,
  OpenInNew,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
// Components
import { PageTemplate } from 'src/shared/templates/pageTemplate';
import { Search } from 'src/shared/components/atoms/search';
import SkeletonList from 'src/shared/components/skeletons/skeletonList';
import EmptyDataComponent from 'src/shared/components/organisms/emptyDataComponent';
import { ChildItemsGrid } from 'src/shared/components/organisms/childItemsGrid';
import { DocumentItemComponent } from 'src/modules/document/components/documentItem';
import ActionMenu from 'src/shared/components/atoms/actionMenu';
import { ActionButtons } from 'src/shared/components/atoms/actionButtons';
import { GenerateActionButtonIcons } from 'src/shared/components/atoms/generateActionButtonsIcons';
import ConfirmDialogModal from 'src/shared/components/atoms/confirmDialog';
// Services
import { IParams } from 'src/shared/interfaces/params';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import { DocumentAPI } from 'src/shared/services/api/documentAPI';
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { DocumentResponse } from 'src/shared/services/api/response/documentResponse';
import { documentUrl, fileUrl } from 'src/shared/services/utils/url';
import { RootStateType } from 'src/shared/services/redux/reducers';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import {
  GetAdminPermission,
  useCanViewStats,
} from 'src/shared/helpers/permissionHelper';
// import DocumentViewerModal from 'src/modules/documents/documentViewer/documentViewerModal';
import { IDocument } from '@cyntler/react-doc-viewer';
import { StatsRangeBadge } from 'src/shared/components/atoms/statsRangeBadge';
import { useContentStats } from 'src/shared/helpers/hooks/useContentStats';
import { useDocumentStatsBatch } from 'src/shared/helpers/hooks/useDocumentStatsBatch';
import { useLogContentView } from 'src/shared/helpers/hooks/useLogContentView';
import { formatBytes } from 'src/shared/helpers/formatBytes';
// import FileDropZone from 'src/shared/components/form/fileDropZone';

const OPENABLE_MIMES = new Set([
  'application/pdf',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'text/csv',
  'text/html',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
]);

const DOWNLOAD_REQUIRED_EXTENSIONS = 'ZIP, RAR, 7Z, XLS, XLSX, XLSM, TXT';

const canOpenFileInBrowser = (mime?: string): boolean => {
  return mime ? OPENABLE_MIMES.has(mime.toLowerCase()) : false;
};

const DocumentListPage = () => {
  const { id } = useParams<IParams>();
  const { t } = useTranslation();
  const history = useHistory();

  // Local states
  const [searchText, setSearchText] = useState('');
  const [userNotAllowed, setUserNotAllowed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const [showActions, setShowActions] = useState<boolean>(false);
  const [searchedData, setSearchedData] = useState<DocumentResponse[]>();
  const [menuDocument, setMenuDocument] = useState<DocumentResponse>();
  const [item, setItem] = useState<ContentPageResponse | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | undefined>();
  const [previewDocumentUri, setPreviewDocumentUri] = useState<
    IDocument | undefined
  >(undefined);
  const [contentStatsRangeDays, setContentStatsRangeDays] =
    useState<number>(365);
  const [documentStatsRangeDays, setDocumentStatsRangeDays] =
    useState<number>(365);
  const [showDocumentInfo, setShowDocumentInfo] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  // Global states
  const { user } = useSelector((state: RootStateType) => state.oidc);
  const { userDetails } = useSelector(
    (state: RootStateType) => state.authState,
  );
  const canViewStats = useCanViewStats(item?.companyId);
  const canDeleteDocument = GetAdminPermission(
    item?.companyId,
    'DeleteDocument',
  );

  const {
    allViews: contentAllViews,
    uniqueViews: contentUniqueViews,
    refetch: refetchContentStats,
  } = useContentStats({
    contentPageId: item?.id,
    rangeDays: contentStatsRangeDays,
    enabled: canViewStats,
  });

  const allNewData =
    searchText.length > 0 ? searchedData : item?.documentList?.documents;

  const documentIds = (allNewData ?? [])
    .map((doc) => Number(doc.id))
    .filter((docId) => Number.isFinite(docId));

  const { statsById: documentStatsById, refetch: refetchDocumentStats } =
    useDocumentStatsBatch({
      documentIds,
      rangeDays: documentStatsRangeDays,
      enabled: canViewStats,
    });
  const getDocumentPageById = useCallback(async () => {
    try {
      const contentApi = new ContentPageAPI();
      const res = await contentApi.Get(id);
      if (res) {
        // controlTagsForUser(res?.result);
        setItem(res?.result);
        await setSelectedImage(
          fileUrl(res?.result?.featuredImageDocumentId, user?.access_token),
        );
      }
    } catch (error) {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    getDocumentPageById();
    const initialLoading = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(initialLoading);
  }, [getDocumentPageById, history]);

  useLogContentView({
    contentPageId: item?.id,
    enabled: true,
    onLogged: refetchContentStats,
  });

  // Så länge inte Get /ContentPage/{id} returnerar 403 om användaren inte matchar sidans taggar gör vi denna koll
  // Kollar endast om någon av användarens taggar matchar sidans taggar, om taggar finns på sidan: pageTags?.length > 0
  // SuperAdmin får se allt därav: userDetails?.permissionGroup?.id !== 5
  // Kollar inte kombinationer role + location etc
  // Tar bort det mest kritiska att vanliga användare inte ska se VD-sidor t ex
  useEffect(() => {
    const controlTagsForUser = (page: any) => {
      setUserNotAllowed(false);
      const userTags = userDetails?.tags ?? [];
      const pageTags = page?.tags ?? [];
      if (pageTags?.length > 0 && userDetails?.permissionGroup?.id !== 5) {
        const matchingTags = pageTags?.filter((p: any) =>
          userTags.some((u: any) => u.id === p.id),
        );
        if (matchingTags?.length === 0) {
          setUserNotAllowed(true);
        } else {
          setUserNotAllowed(false);
        }
      }
    };
    controlTagsForUser(item);
  }, [item, userDetails]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    const newData = item?.documentList?.documents?.filter(
      (newItem: DocumentResponse) => {
        return (
          newItem?.name
            ?.toLocaleLowerCase()
            .indexOf(e.target.value.toLowerCase()) !== -1
        );
      },
    );
    setSearchedData(newData);
  };

  const onDownloadFileClicked = async (file: DocumentResponse) => {
    setFileLoading(true);
    try {
      const res = await axios.get(
        `${fileUrl(file?.id, user?.access_token)}&dl=1`,
        {
          responseType: 'blob',
        },
      );
      if (res) {
        const newUrl = window.URL.createObjectURL(
          new Blob([res?.data], {
            type: res.headers['content-type'],
          }),
        );
        const link = document.createElement('a');
        link.href = newUrl;
        link.setAttribute('dowload', file.name ?? 'Dokument');
        document.body.appendChild(link);
        link.download = file.name ?? 'Dokument';
        link.click();
        if (file?.id) {
          const documentApi = new DocumentAPI();
          documentApi
            .LogDownloadV2(file.id, 'web')
            .then(() => refetchDocumentStats())
            .catch(() => undefined);
        }
        setFileLoading(false);
        toast.success(`${t('DownloadDone')}:
        ${file.name}`);
      }
    } catch (error) {
      setFileLoading(false);
      toast.error(`${t('DownloadFailed')}:
      ${file.name}`);
    }
  };

  // const onSingleFileChangeFeaturedDocument = useCallback((files: File[]) => {
  //   if (files.length > 0) {
  //     setSelectedFile(files[0]);
  //   } else setSelectedFile(undefined);
  // }, []);

  const handleClickShowActionMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    itemToDelete: DocumentResponse,
  ) => {
    setMenuDocument(itemToDelete);
    setAnchorEl(event.currentTarget);
    setShowActions(true);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
    setShowActions(false);
  };

  const onOpenFileClicked = (file: DocumentResponse) => {
    if (!file?.id) {
      toast.error(t('CantOpen'));
      return;
    }
    if (!canOpenFileInBrowser(file.mimeType)) {
      onDownloadFileClicked(file);
      return;
    }

    window.open(
      fileUrl(file.id, user?.access_token),
      '_blank',
      'noopener,noreferrer',
    );
    if (file?.id) {
      const documentApi = new DocumentAPI();
      documentApi
        .LogDownloadV2(file.id, 'web')
        .then(() => refetchDocumentStats())
        .catch(() => undefined);
    }
  };

  const onOpenFileFromMenuClicked = () => {
    if (menuDocument) {
      onOpenFileClicked(menuDocument);
    }
    handleCloseActionMenu();
  };

  const onDownloadFileFromMenuClicked = () => {
    if (menuDocument) {
      onDownloadFileClicked(menuDocument);
    }
    handleCloseActionMenu();
  };

  const onShowFileInfoClicked = () => {
    if (!menuDocument) {
      return;
    }
    setShowDocumentInfo(true);
    handleCloseActionMenu();
  };

  const copyToClipboard = async (
    value: string,
    successMessage: string,
  ): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage, {
        theme: 'colored',
      });
    } catch (error) {
      toast.error(t('LinkNotCopied'), {
        theme: 'colored',
      });
    }
  };

  const onCopyDocumentIdClicked = async () => {
    if (!menuDocument?.id) {
      handleCloseActionMenu();
      return;
    }
    await copyToClipboard(menuDocument.id.toString(), t('DocumentIdCopied'));
    handleCloseActionMenu();
  };

  const onCopyDocumentUrlClicked = async () => {
    if (!menuDocument?.id) {
      handleCloseActionMenu();
      return;
    }
    const docUrl = documentUrl(menuDocument.id);
    await copyToClipboard(docUrl, t('DocumentUrlCopied'));
    handleCloseActionMenu();
  };

  const onDeleteDocumentClicked = () => {
    setShowDeleteDialog(true);
    handleCloseActionMenu();
  };

  const deleteDocument = async () => {
    if (!menuDocument?.id) {
      setShowDeleteDialog(false);
      handleCloseActionMenu();
      return;
    }
    try {
      const documentApi = new DocumentAPI();
      await documentApi.deleteDocumentWithQureryID(menuDocument.id);
      getDocumentPageById();
      setShowDeleteDialog(false);
      handleCloseActionMenu();
      toast.success(t('Deleted!'), {
        theme: 'colored',
      });
    } catch (error) {
      setShowDeleteDialog(false);
      handleCloseActionMenu();
      toast.error(t('SomethingWentWrong'));
    }
  };

  const selectedDocumentOpenable = canOpenFileInBrowser(menuDocument?.mimeType);

  return (
    <PageTemplate
      header={item?.title ?? ''}
      headerMeta={
        canViewStats ? (
          <StatsRangeBadge
            label={t('ViewsLabel')}
            rangeDays={contentStatsRangeDays}
            value={contentAllViews}
            uniqueValue={contentUniqueViews}
            onRangeChange={setContentStatsRangeDays}
          />
        ) : undefined
      }
      featuredImage={selectedImage}
      featuredImageText={item?.featuredImageText ?? ''}
      onImageError={() => setSelectedImage(undefined)}
      headerContent={userNotAllowed ? '' : item?.content ?? ''}
      showTags
      tags={item?.tags}
      businessAreaTags={item?.businessAreaTags}
      content={
        <>
          {userNotAllowed ? (
            <Typography sx={{ p: 1, fontStyle: 'italic' }}>
              {t('NoAccessToContent')}
            </Typography>
          ) : (
            <>
              <Search
                value={searchText}
                placeholder={t('Search')}
                onTextChange={(e) => handleSearch(e)}
                onClearText={() => setSearchText('')}
              />
              <Divider />
              {loading ? (
                <SkeletonList rows={3} />
              ) : (
                <>
                  <List>
                    {fileLoading && (
                      <div
                        style={{ position: 'absolute', top: 0, width: '100%' }}
                      >
                        <LinearProgress />
                      </div>
                    )}
                    {allNewData && allNewData?.length > 0 ? (
                      allNewData
                        .sort((a: any, b: any) => a.name.localeCompare(b.name))
                        .map((newDataItem: DocumentResponse) => (
                          <DocumentItemComponent
                            handleClickShowActionMenu={(e) =>
                              handleClickShowActionMenu(e, newDataItem)
                            }
                            key={newDataItem.id}
                            item={newDataItem as DocumentResponse}
                            onDownloadFileClicked={onDownloadFileClicked}
                            onOpenFileClicked={onOpenFileClicked}
                            enableStatsRange={canViewStats}
                            rangeDays={documentStatsRangeDays}
                            onRangeChange={setDocumentStatsRangeDays}
                            allDownloads={
                              documentStatsById[Number(newDataItem.id)]?.all ??
                              null
                            }
                            uniqueDownloads={
                              documentStatsById[Number(newDataItem.id)]
                                ?.unique ?? null
                            }
                          />
                        ))
                    ) : (
                      <EmptyDataComponent text={t('NoDocumentsFound')} />
                    )}
                    {allNewData && allNewData.length > 0 && (
                      <Typography
                        sx={{
                          textAlign: 'center',
                          mt: 2,
                          fontSize: 14,
                        }}
                      >
                        {t('Total{number}Documents', {
                          number: allNewData?.length,
                        })}
                      </Typography>
                    )}
                  </List>
                  {item?.childItems && item?.childItems?.length > 0 && (
                    <ChildItemsGrid childItems={item.childItems} />
                  )}
                </>
              )}
            </>
          )}
          <ActionMenu
            showActions={showActions}
            anchorEl={anchorEl}
            handleClose={handleCloseActionMenu}
          >
            <MenuItem onClick={onOpenFileFromMenuClicked}>
              <ListItemIcon>
                <OpenInNew fontSize='small' />
              </ListItemIcon>
              {t('OpenInNewTab')}
            </MenuItem>
            <MenuItem onClick={onDownloadFileFromMenuClicked}>
              <ListItemIcon>
                <Download fontSize='small' />
              </ListItemIcon>
              {t('Download')}
            </MenuItem>
            <MenuItem onClick={onShowFileInfoClicked}>
              <ListItemIcon>
                <InfoOutlined fontSize='small' />
              </ListItemIcon>
              {t('ShowInformation')}
            </MenuItem>
            <MenuItem onClick={onCopyDocumentIdClicked}>
              <ListItemIcon>
                <ContentCopy fontSize='small' />
              </ListItemIcon>
              {t('CopyDocumentId')}
            </MenuItem>
            <MenuItem onClick={onCopyDocumentUrlClicked}>
              <ListItemIcon>
                <LinkIcon fontSize='small' />
              </ListItemIcon>
              {t('CopyDocumentUrl')}
            </MenuItem>
            {canDeleteDocument && (
              <MenuItem onClick={onDeleteDocumentClicked}>
                <ListItemIcon>
                  <DeleteOutline fontSize='small' />
                </ListItemIcon>
                {t('deleteFile')}
              </MenuItem>
            )}
          </ActionMenu>
          <ActionButtons
            items={GenerateActionButtonIcons(
              // Create
              undefined,
              // Edit
              GetAdminPermission(item?.companyId)
                ? `/DocumentListPage/${id}/edit`
                : undefined,
              // Pin
              GetAdminPermission(item?.companyId)
                ? () => ConsoleHelper.log('Pin item', id)
                : undefined,
            )}
          />
          <Dialog
            open={showDocumentInfo}
            onClose={() => setShowDocumentInfo(false)}
            fullWidth
            maxWidth='xs'
          >
            <DialogTitle>{t('InformationAboutDocument')}</DialogTitle>
            <DialogContent dividers>
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('FileName')}:</strong>{' '}
                {menuDocument?.name ?? t('Unknown')}
              </Typography>
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('FileType')}:</strong>{' '}
                {menuDocument?.mimeType ?? t('Unknown')}
              </Typography>
              <Typography variant='body2' sx={{ mb: 1 }}>
                <strong>{t('DocumentId')}:</strong>{' '}
                {menuDocument?.id?.toString() ?? t('Unknown')}
              </Typography>
              <Typography variant='body2'>
                <strong>{t('FileSize')}:</strong>{' '}
                {menuDocument?.size !== undefined && menuDocument?.size !== null
                  ? formatBytes(menuDocument.size)
                  : t('Unknown')}
              </Typography>
              <Typography variant='body2' sx={{ mt: 2, mb: 1 }}>
                <strong>{t('FileAction')}:</strong>{' '}
                {selectedDocumentOpenable
                  ? t('CanOpenInNewTab')
                  : t('NeedsDownloadToOpen')}
              </Typography>
              <Typography variant='body2'>
                <strong>{t('DownloadRequiredFileTypes')}:</strong>{' '}
                {DOWNLOAD_REQUIRED_EXTENSIONS}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDocumentInfo(false)}>
                {t('Close')}
              </Button>
            </DialogActions>
          </Dialog>
          <ConfirmDialogModal
            open={showDeleteDialog}
            dialogTitle={t('Confirm')}
            dialogText={t('ConfirmDeleting{item}', {
              item: menuDocument?.name ?? t('deleteFile'),
            })}
            handleClose={() => setShowDeleteDialog(false)}
            confirmButtonClick={deleteDocument}
            confirmTextButton={t('OK')}
            cancelTextButton={t('Cancel')}
          />
        </>
      }
    />
  );
};
export default DocumentListPage;
